import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, ArrowDown, Zap } from "lucide-react";
import { MaskedLine } from "./motion";
import { scrollToId } from "../lib/site";
import { useContent } from "../lib/content";

const BADGES = ["Free Visit", "Free Measurement", "Samples at Your Doorstep", "Fast Installation"];

export default function Hero() {
  const { hero } = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 50]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-32 lg:pt-40 pb-14 lg:pb-20">
      <div className="pointer-events-none absolute -top-24 -right-24 w-[480px] h-[480px] rounded-full bg-reef-light/60 blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-mono text-[11px] sm:text-xs tracking-[0.3em] uppercase text-reef font-semibold mb-6 flex items-center gap-3"
          >
            <span className="inline-block w-8 h-px bg-reef" />
            {hero.eyebrow}
          </motion.p>

          <h1
            data-testid="hero-title"
            className="font-serif font-light tracking-tight leading-[1.04] text-slate-900 text-[13.5vw] sm:text-6xl lg:text-7xl"
          >
            <MaskedLine delay={0.15}>{hero.line1}</MaskedLine>
            <MaskedLine delay={0.28}>{hero.line2}</MaskedLine>
            <MaskedLine delay={0.41}>
              <span className="italic text-reef">{hero.line3}</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            {hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-book-visit-cta"
              onClick={() => scrollToId("book")}
              className="group px-7 py-4 rounded-full bg-reef text-sand-bg font-mono text-xs tracking-[0.18em] uppercase hover:bg-reef-dark transition-colors duration-300 flex items-center gap-3"
            >
              Book a Free Visit
              <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <button
              data-testid="hero-calculator-cta"
              onClick={() => scrollToId("estimator")}
              className="px-7 py-4 rounded-full border border-reef/30 text-reef font-mono text-xs tracking-[0.18em] uppercase hover:border-reef hover:bg-reef-light transition-all duration-300"
            >
              Estimate Calculator
            </button>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-2"
          >
            {BADGES.map((b) => (
              <li key={b} className="flex items-center gap-2 text-sm text-slate-600">
                <Check className="w-4 h-4 text-reef" strokeWidth={2.5} />
                {b}
              </li>
            ))}
          </motion.ul>
        </motion.div>

        <div className="lg:col-span-5 relative mt-6 lg:mt-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 sm:-inset-4 border border-reef/25 rounded-t-[999px] rounded-b-[28px]" />
            <div className="relative overflow-hidden rounded-t-[999px] rounded-b-[28px] aspect-[4/5] shadow-[0_40px_80px_-30px_rgba(134,95,52,0.35)]">
              <motion.img
                src={hero.image}
                alt="Premium custom curtains by Reef Deco Dubai"
                data-testid="hero-image"
                style={{ y: imgY, scale: 1.18 }}
                className="w-full h-full object-cover"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.1 }}
              className="absolute -bottom-6 -left-4 sm:-left-8 bg-white/90 backdrop-blur-xl border border-sand-border rounded-2xl px-5 py-4 shadow-[0_20px_50px_-20px_rgba(134,95,52,0.3)] flex items-center gap-4"
            >
              <div className="w-10 h-10 rounded-full bg-reef flex items-center justify-center">
                <Zap className="w-5 h-5 text-sand-bg" />
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">{hero.badge_label}</p>
                <p className="font-serif text-lg text-reef leading-tight">{hero.badge_title}</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
